import Branch from "@NA/Icons/Branch";
import CrossIcon from "@NA/Icons/CrossIcon";
import HomeBranch from "@NA/Icons/HomeBranch";
import MyBranchState from "@NA/Store/Data/ADIMyBranchState";
import ADIColors from "@NA/Utils/ADIColors";
import ADIChangeBranch from "@NA/Widgets/ProductList/ADIChangeBranch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { Icon, Typography } from "@insite/mobius/index";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    title?: TypographyProps;
    count?: TypographyProps;
    facetName?: TypographyProps;
    filterText?: TypographyProps;
    myBranchWrapper?: InjectableCss;
    myBranchName?: TypographyProps;
    changeBranchLinkGrid?: InjectableCss;
    changeBranchLink?: TypographyProps;
    changeBranchModal?: ModalPresentationProps;
    iconTextWrapper?: InjectableCss;
    branchTxt?: TypographyProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: center;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-word;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: center;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    title: {
        css: css`
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            color: ${getColor("text.main")};
        `,
    },
    count: {
        css: css`
            background-color: ${getColor("text.disabled")};
            border-radius: 4px;
            padding: 2px 3px;
            color: #666;
            margin-left: 5px;
        `,
    },
    facetName: {
        css: css`
            color: #000 !important;
            font-weight: normal !important;
        `,
    },
    myBranchWrapper: {
        css: css`
            margin: 0 0 10px 30px;
        `,
    },
    myBranchName: {
        size: 14,
        weight: "bold",
        color: ADIColors.black,
    },
    changeBranchLinkGrid: {
        css: css`
            order: initial;
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            order: 4;
                            margin-bottom: 20px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            > span {
                margin-right: 10px;
            }
        `,
    },
    changeBranchLink: {
        css: css`
            font-size: ${getProp("theme.typography.h6.size")};
            margin-left: 10px;
            a:hover {
                text-decoration: underline;
            }
            span {
                font-size: ${getProp("theme.typography.h6.size")};
                display: inline;
                margin-top: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: ${getProp("theme.typography.headerSecondary.size")};
                                display: block;
                                margin-top: 6px;
                            `,
                            css``,
                            css``,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    changeBranchModal: {
        sizeVariant: "large",
        closeButtonIconProps: {
            src: CrossIcon,
            size: 20,
        },
        cssOverrides: {
            modalContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            align-items: flex-start;
                        `,
                        css`
                            align-items: flex-start;
                        `,
                        null,
                        null,
                        null,
                    ])}
            `,
            modalBody: css`
                max-width: 750px !important;
                border-radius: 4px;
                padding: 28px !important;
                height: auto !important;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                top: 10% !important;
                                max-width: 90% !important;
                                border-radius: 0px;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
            `,
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                border-bottom: 0;
                padding: 0 !important;
                margin-bottom: 20px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                margin-bottom: 0px;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
                > h4 {
                    font-size: 16px;
                    font-weight: bold;
                    font-family: "Nunito Sans", sans-serif;
                    line-height: normal;
                    color: ${ADIColors.black2};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
                button {
                    border-radius: 0;
                    position: absolute;
                    top: 5px !important;
                    right: 12px !important;
                    svg {
                        height: 20px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    height: 15px;
                                `,
                                css`
                                    height: 15px;
                                `,
                                null,
                                null,
                                null,
                            ])}
                        path {
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        fill: #c60b2b !important;
                                    `,
                                    css`
                                        fill: #c60b2b !important;
                                    `,
                                    null,
                                    null,
                                    null,
                                ])}
                        }
                    }
                }
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    iconTextWrapper: {
        css: css`
            display: flex;
            padding: 7px;
        `,
    },
    branchTxt: {
        size: 14,
        weight: 700,
        css: css`
            line-height: normal;
            margin: 5px 0 0 10px;
            color: #c71e1e;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel, title?: string) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
    myBranchDetails: MyBranchState;
    isAuthenticated: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    myBranchDetails,
    isAuthenticated,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const anySelected = facets.find(f => f.selected) !== undefined;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [availabilityFacets, setAvailabilityFacets] = React.useState<FacetModel[]>();
    const [brandFacets, setBrandFacets] = React.useState<FacetModel[]>();
    const [allFacets, setAllFacets] = React.useState<FacetModel[]>();
    const limitedBrandFacets = expanded ? brandFacets : brandFacets?.slice(0, showMoreLimit);
    const limitedAvailabilityFacets = expanded ? availabilityFacets : availabilityFacets?.slice(0, showMoreLimit);
    const limitedAllFacets = expanded ? allFacets : allFacets?.slice(0, showMoreLimit);
    const [path, setPath] = useState("");

    React.useEffect(() => {
        if (typeof window !== "undefined") {
            setPath(window.location.pathname);
        }
    }, []);

    React.useEffect(() => {
        const totalItemsCount = facets.reduce((acc, item) => (acc += item.count), 0);
        setTotalCount(totalItemsCount);
        if (title === "Availability") {
            const availabilityFacets = facets.filter(
                facet =>
                    facet.name === "In Stock" ||
                    facet.name === "Special Order" ||
                    facet.name === myBranchDetails.warehouseCode,
            );
            setAvailabilityFacets(availabilityFacets);
        }
        if (title === "Brand Name") {
            const brandAscending = [...facets].sort((a, b) =>
                a.name.split("~")[1].toLowerCase() > b.name.split("~")[1].toLowerCase() ? 1 : -1,
            );
            setBrandFacets(brandAscending);
        }

        if (title !== "Availability" && title !== "Brand Name") {
            const limitedFacets = [...facets].sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
            setAllFacets(limitedFacets);
        }
    }, [facets]);

    React.useEffect(() => {
        if (title === "Availability") {
            const availabilityFacets = facets.filter(
                facet =>
                    facet.name === "In Stock" ||
                    facet.name === "Special Order" ||
                    facet.name === myBranchDetails.warehouseCode,
            );
            const availabilityTotalItemsCount = availabilityFacets.reduce((acc, item) => (acc += item.count), 0);
            setTotalCount(availabilityTotalItemsCount);
        }
    }, [facets, myBranchDetails]);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setModalIsOpen(true);
    };

    const modelHeadline = siteMessage("My Branch to Check Availability");

    if (!isAuthenticated && title === "Availability") {
        return null;
    }


    const displayTitle = (title: string) => {
        // Suppress 'Category' or 'Subcategory' only if URL contains 'shopproducts'
        if (path.includes("shopproducts") && (title === "Category" || title === "Subcategory")) {
            return null;
        }
        return title;
    };

    const Categories = !(path.includes("shopproducts") && (title === "Category" || title === "Subcategory"));

    return (
        <>
        {Categories && <ManagedAccordionSection
            title={
                <StyledWrapper>
                    <Typography {...styles.title}>{displayTitle(title)}</Typography>
                    <Typography {...styles.count}>{Categories && totalCount}</Typography>
                </StyledWrapper>
            }
            {...styles.accordionSection}
            //ADINA-56530
            // initialExpanded={expandByDefault || anySelected}
            initialExpanded={(title !== "Availability" ? expandByDefault : true) || anySelected}
        >
            {limitedAllFacets?.map(facet => (
                <>
                    {title !== "Availability" && title !== "Brand Name" && Categories && (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                <Typography {...styles.facetName}>{facet.name}</Typography>
                                {facet.count !== -1 && <Typography {...styles.count}>{facet.count}</Typography>}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                </>
            ))}
            {limitedBrandFacets?.map(facet => (
                <>
                    {title === "Brand Name" && Categories  && (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                {<Typography {...styles.facetName}>{facet.name.includes("~") ? facet.name.split("~")[1]  : facet.name}</Typography>}
                                {facet.count !== -1 && <Typography {...styles.count}>{facet.count}</Typography>}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                </>
            ))}
            {limitedAvailabilityFacets?.map(facet => (
                <>
                    {title === "Availability" && Categories && (facet.name === "In Stock" || facet.name === "Special Order") && (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                <Typography {...styles.facetName}>{facet.name}</Typography>
                                {facet.count !== -1 && <Typography {...styles.count}>{facet.count}</Typography>}
                            </Checkbox>
                        </CheckboxGroup>
                    )}
                </>
            ))}
            {limitedAvailabilityFacets?.map(facet => (
                <>
                    {title === "Availability" && Categories && facet.name === myBranchDetails.warehouseCode && (
                        <>
                            <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                                <Checkbox
                                    {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                    checked={facet.selected}
                                    onChange={() => onChangeFacet(facet, title)}
                                    data-test-selector={`facetFilter${facet.id}`}
                                >
                                    {translate("In My Branch")}
                                    {facet.count !== -1 && <Typography {...styles.count}>{facet.count}</Typography>}
                                </Checkbox>
                            </CheckboxGroup>
                            <StyledWrapper {...styles.myBranchWrapper}>
                                <Typography {...styles.myBranchName}>{myBranchDetails.warehouseDisplayName}</Typography>
                                <Typography {...styles.changeBranchLinkGrid}>
                                    <Typography {...styles.changeBranchLink}>
                                        <Link onClick={viewAvailabilityByWarehouseClickHandler}>
                                            {translate("Change")}
                                        </Link>
                                    </Typography>
                                    <Modal
                                        {...styles.changeBranchModal}
                                        headline={modelHeadline}
                                        isOpen={modalIsOpen}
                                        handleClose={modalCloseHandler}
                                    >
                                        <StyledWrapper {...styles.iconTextWrapper}>
                                            <Icon size={26} src={HomeBranch} />
                                            <Typography {...styles.branchTxt}>Branch</Typography>
                                        </StyledWrapper>
                                        <ADIChangeBranch
                                            myBranchDetail={myBranchDetails.warehouseDisplayName}
                                            modalCloseHandler={modalCloseHandler}
                                        />
                                    </Modal>
                                </Typography>
                            </StyledWrapper>
                        </>
                    )}
                </>
            ))}
            {facets &&
                facets.length > showMoreLimit &&
                title !== "Availability" &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("See All")}
                    </Link>
                ))}
        </ManagedAccordionSection>}
        </>
    );
};

export default ProductListFiltersAccordionSection;
